import { sitecoreAxios } from '@pm2/api';
import { configRefs } from '@utils/constants';
import { CategoryModel } from './bookshelf-component/models/CategoryModel.js';
import { BookShelfModel } from './bookshelf-component/models/BookShelfModel.js';
import { BookModel } from './bookshelf-component/models/BookModel.js';

/** Format of the response data */

/** Format of a single book response */

/** Specification of the headers required to interact with the PM2 apis */

/**
 * Helper method that fetches data from pm2, and turns it into a well-defined
 * BookShelfModel that's ready for rendering the bookshelf component.
 */
async function getBookshelf(portalId, shelfId, options) {
  // Determine wheter to reach out to preview or ordinary endpoints
  const baseUrl = options?.previewMode ? await configRefs.apis.pm2.preview.get() : await configRefs.apis.pm2.url.get();

  // Fetch data from the associated pm2 api
  const response = await sitecoreAxios.get(`${baseUrl}/api/v1/portals/${portalId}/shelves/${shelfId}`);

  // Prepare filter dependency configuration
  const disableAlphabeticGroups = response.data.enableAlphabeticGroups === false;
  let categories = [];
  if (!disableAlphabeticGroups) {
    // Find the starting letters used within this shelf
    const startingLetters = new Map();
    for (const book of response.data.books) {
      // Ignore books with no starting letter
      if (book.title.length === 0) {
        continue;
      }

      // Get the starting letter
      const startingLetter = book.title.charAt(0).toUpperCase();

      // If no category has been added for this letter yet, then do so
      // now...
      if (!startingLetters.has(startingLetter)) {
        startingLetters.set(startingLetter, new CategoryModel({
          id: startingLetter,
          title: startingLetter
        }));
      }
    }

    // Extract categories, and sort them alphabetically
    categories = Array.from(startingLetters.values()).sort((a, b) => a.title > b.title ? 1 : -1);
  }

  // Parse book models
  const books = await Promise.all(response.data.books.map(book => mapBookModel(book, disableAlphabeticGroups)));

  // Create the well-formatted model
  return Object.assign(new BookShelfModel({
    books,
    // Categories are currently not supported by the PM2 backend, so
    // simply hardcode to an empty list of category models...
    categories,
    categorizeItems: true,
    // propagate filter settings
    disableFilters: response.data.enableFilters === false,
    disableLetAndLixFilters: response.data.enableLetAndLixFilters === false,
    disableSearch: response.data.enableSearch === false,
    // Apply the filter independencies specified above
    filterIndependencies: [],
    // Propagate the id "as is"
    id: shelfId,
    // propagate filter levels
    gradeFilterLabel: response.data.filterLabels?.gradeFilterLabel,
    levelFilterLabel: response.data.filterLabels?.levelFilterLabel,
    subjectFilterLabel: response.data.filterLabels?.subjectFilterLabel
  }), {
    classLevelCoDependency: response.data.classLevelCoDependency
  });
}

/** Helper that converts book response into a well-formatted book model */
async function mapBookModel(book, disableAlphabeticGroups) {
  const coverSrc = book.cover.src.includes("?") ? `${book.cover.src}&h=${getBookCoverHeight()}` : `${book.cover.src}?h=${getBookCoverHeight()}`;
  return BookModel.DESERIALIZE({
    authors: book.authors,
    badge: book.badge,
    category: book.title.length > 0 && !disableAlphabeticGroups ? book.title.charAt(0).toUpperCase() : undefined,
    cover: {
      ...book.cover,
      src: coverSrc
    },
    description: book.description,
    grades: book.grades,
    id: cleanUpId(book.id),
    isbn: book.isbn,
    letComplexity: book.letNumber,
    levels: book.levels,
    lixComplexity: book.lixNumber,
    relatedTask: book.relatedTask,
    seriesTitle: book.seriesTitle,
    subjects: book.subjects,
    title: book.title,
    url: book.url
  });
}
function cleanUpId(id) {
  return id.replace(/^\{/, "").replace(/\}$/, "").toLowerCase();
}

/** Calculation of optimal cover height based on current device */
let bookCoverHeight = undefined;
function getBookCoverHeight() {
  return bookCoverHeight ??= Math.min(370, Math.round(Math.round(window.devicePixelRatio * 2) / 2 * 300));
}

export { getBookshelf };
//# sourceMappingURL=api.get-bookshelf.js.map
